import { useNavigate } from "react-router-dom";
import { useAuth, apiFetch } from "../context/AuthContext.js";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const LOGOUT_TIMEOUT_MS = 8000;

export const useLogout = () => {
  const navigate = useNavigate();
  const { logout: authLogout } = useAuth();

  const logout = async () => {
    console.group("🔴 LOGOUT SEQUENCE STARTED");
    console.time("logout-duration");

    // ── Step 1: Hit the backend ──────────────────────────────────────────────
    let backendSuccess = false;
    try {
      console.log("📡 Step 1 — Calling backend logout...");

      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
        console.warn("⏱️ Backend logout request timed out after", LOGOUT_TIMEOUT_MS, "ms");
      }, LOGOUT_TIMEOUT_MS);

      const response = await apiFetch(`${BASE_URL}/users/logout`, {
        method: "POST",
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      console.log("📡 Backend response status:", response.status);

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        console.log("✅ Backend logout succeeded:", data);
        backendSuccess = true;
      } else {
        const errData = await response.json().catch(() => ({}));
        console.warn("⚠️ Backend returned non-OK status:", response.status, errData);
      }
    } catch (err) {
      if (err.name === "AbortError") {
        console.warn("⏱️ Backend logout aborted due to timeout.");
      } else {
        console.warn("❌ Backend logout threw an error:", err.message);
      }
    }

    // ── Step 2: Clear client state regardless ───────────────────────────────
    console.log("🧹 Step 2 — Clearing client state...");
    try {
      authLogout();
      console.log("✅ authLogout() called successfully.");
    } catch (err) {
      console.error("❌ authLogout() threw:", err.message);
    }

    // ── Step 3: Nuke all storage ─────────────────────────────────────────────
    console.log("🗑️ Step 3 — Wiping storage...");
    try {
      const lsKeysBefore = Object.keys(localStorage);
      localStorage.clear();
      sessionStorage.clear();
      console.log("✅ Cleared localStorage keys:", lsKeysBefore);
      console.log("✅ sessionStorage cleared.");
    } catch (err) {
      console.error("❌ Storage clear failed:", err.message);
    }

    // ── Step 4: Verify storage is actually empty ─────────────────────────────
    const remainingLS = Object.keys(localStorage);
    const remainingSS = Object.keys(sessionStorage);
    if (remainingLS.length > 0 || remainingSS.length > 0) {
      console.warn("⚠️ Storage not fully cleared!", { remainingLS, remainingSS });
    } else {
      console.log("✅ Storage fully cleared — no keys remain.");
    }

    // ── Step 5: Navigate ─────────────────────────────────────────────────────
    console.log("🚀 Step 5 — Navigating to /login...");
    console.timeEnd("logout-duration");
    console.groupEnd();

    // Small flush delay so React state settles before navigation
    await new Promise((resolve) => setTimeout(resolve, 50));
    navigate("/login", { replace: true });

    // ── Step 6: Final safety net ─────────────────────────────────────────────
    // If navigate() somehow fails (e.g., router not mounted), force a hard redirect
    setTimeout(() => {
      if (!window.location.pathname.startsWith("/login")) {
        console.warn("🚨 navigate() may have failed — forcing hard redirect.");
        window.location.replace("/login");
      }
    }, 500);

    return { backendSuccess };
  };

  return { logout };
};